import { blattOffen } from "./schliessanfrage";

/* ══════════════════════════════════════════════════════════════════
   Wird hier gerade etwas bearbeitet?

   Luca, 19.09.2026: *„Ich möchte, dass es automatisch aktualisiert wird."*
   Eine neue Fassung wird seitdem ohne Knopf übernommen
   (`pwa/anmelden.ts`) — und das heißt: neu laden, ohne dass jemand darum
   gebeten hat. Das darf **nie** etwas kosten, was jemand getippt hat. Diese
   Datei ist die eine Stelle, die das beantwortet, damit die Antwort nicht in
   jeder Maske einzeln erraten wird.

   ── Zwei Fragen, nicht eine ───────────────────────────────────────

   | Frage | was sie umfasst | wer sie stellt |
   |---|---|---|
   | `schreibtNoch()` | eine Anfrage an den Server unterwegs, ein Entwurf, der noch nicht im Gerät steht | **jeder** Moment, auch der Wegwechsel |
   | `wirdBearbeitet()` | ein offenes Blatt, der Finger in einem Feld, an dieser Maske seit dem letzten Wegwechsel etwas getan | die Momente **ohne** Wegwechsel: Hintergrund, Rückkehr, lange Ruhe |

   Beim **Wegwechsel** zählt nur die erste: Wer auf eine andere Maske geht,
   verlässt die, an der er gearbeitet hat, ohnehin — sie wird abgebaut, mit
   oder ohne neue Fassung. Was dort nicht gesichert war, war es vorher auch
   nicht. Was aber gerade **unterwegs** ist (ein Stempel, ein Protokoll auf
   dem Weg zum Server, der Entwurf in den letzten 400 ms vor dem Schreiben),
   darf ein Neuladen nicht abschneiden.

   ── „An der Maske etwas getan" ────────────────────────────────────

   Ein Klick, ein Tastendruck, eine Eingabe in ein Feld — seit dem letzten
   Wechsel des Pfads. **Nicht** Rollen und Wischen: Wer eine Liste liest,
   arbeitet nicht an ihr. Das ist absichtlich grob. Die Masken müssten es
   sonst einzeln melden, und die eine, die es vergisst, ist die, in der
   jemand gerade eine Zahl eintippt. Grob heißt hier: im Zweifel **später**
   neu laden — beim nächsten Wegwechsel —, nie im Zweifel früher.

   **Deshalb gibt es auch keinen Haken, mit dem eine Maske „ich bin in
   Arbeit" meldet.** Alles, was eine Maske an Ungesichertem tragen kann,
   entsteht durch einen Klick, einen Tastendruck oder liegt in einem Blatt
   (Deckblatt, Blatt von unten, Vollbild mit der Unterschrift — alle drei
   stehen im Stapel der Schließanfrage, `schale/schliessanfrage.ts`).

   Die Protokolle am Telefon brauchen es dabei am wenigsten: Ihr Entwurf
   steht nach 400 ms im Gerät (`daten/entwurf.ts`) und meldet sich hier,
   solange er es noch nicht tut.
   ══════════════════════════════════════════════════════════════════ */

/** Was gerade zum Server oder ins Gerät unterwegs ist — je Melder ein Eintrag. */
const unterwegs = new Set<unknown>();
/** Wer außerdem gefragt wird, ob noch geschrieben wird (die Mutationen, `main.tsx`). */
const schreibquellen = new Set<() => boolean>();

let bearbeitetSeitWegwechsel = false;
let pfad: string | null = null;
/** Wann sich zuletzt jemand geregt hat — `0` heißt: seit dem Laden niemand. */
let letzteRegung = 0;
let horcht = false;

/** Meldet (oder nimmt zurück), dass etwas unterwegs ist, das kein Neuladen abschneiden darf. */
export function schreibenMelden(wer: unknown, laeuft: boolean): void {
  if (laeuft) unterwegs.add(wer);
  else unterwegs.delete(wer);
}

/** Eine weitere Quelle für „wird noch geschrieben?" — gibt die Abmeldung zurück. */
export function schreibquelle(frage: () => boolean): () => void {
  schreibquellen.add(frage);
  return () => { schreibquellen.delete(frage); };
}

/** Ist gerade etwas zum Server oder ins Gerät unterwegs? */
export function schreibtNoch(): boolean {
  if (unterwegs.size > 0) return true;
  for (const frage of schreibquellen) if (frage()) return true;
  return false;
}

/** Steht der Finger in einem Feld, in das man tippt? */
function tipptGerade(): boolean {
  if (typeof document === "undefined") return false;
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (!(el instanceof HTMLInputElement)) return false;
  return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file", "image", "hidden"]
    .includes(el.type);
}

/** Wird an dieser Maske gerade gearbeitet — so, dass ein Neuladen etwas kosten könnte? */
export function wirdBearbeitet(): boolean {
  return bearbeitetSeitWegwechsel || blattOffen() || tipptGerade();
}

/** Nichts unterwegs, nichts in Arbeit — ein Neuladen kostet nichts. */
export function istRuhig(): boolean {
  return !schreibtNoch() && !wirdBearbeitet();
}

/** Hat sich seit diesem Zeitpunkt jemand geregt — getippt, geklickt, gerollt? */
export function regungSeit(zeitpunkt: number): boolean {
  return letzteRegung > zeitpunkt;
}

/** Wie lange sich niemand geregt hat, in Millisekunden. */
export function stilleSeit(): number {
  return Date.now() - letzteRegung;
}

/**
 * Der Pfad hat gewechselt — die Maske von vorher ist abgebaut.
 *
 * Gleicher Pfad mit anderen Suchwerten ist **kein** Wechsel: Ein Filter
 * schickt niemanden weg, und was im Formular daneben steht, steht noch da.
 */
export function wegGewechselt(neu: string): void {
  if (pfad !== null && neu !== pfad) bearbeitetSeitWegwechsel = false;
  pfad = neu;
}

/**
 * Hängt die Horcher an das Dokument — einmal, beim Start (`main.tsx`).
 *
 * In der Fangphase: Eine Maske, die ein Ereignis anhält, soll es hier
 * trotzdem nicht verschlucken.
 */
export function ruheHorchen(): void {
  if (horcht || typeof document === "undefined") return;
  horcht = true;
  const gearbeitet = () => {
    bearbeitetSeitWegwechsel = true;
    letzteRegung = Date.now();
  };
  const geregt = () => { letzteRegung = Date.now(); };
  for (const art of ["click", "keydown", "input", "change"]) {
    document.addEventListener(art, gearbeitet, { capture: true, passive: true });
  }
  for (const art of ["pointerdown", "wheel", "scroll", "touchstart"]) {
    document.addEventListener(art, geregt, { capture: true, passive: true });
  }
}
